const getApiBaseUrl = (): string => {
  const envBaseUrl = process.env.REACT_APP_API_BASE_URL;
  if (envBaseUrl && envBaseUrl.trim().length > 0) {
    return envBaseUrl.trim().replace(/\/+$/, '');
  }

  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:3001';
  }

  return '';
};

const buildUrl = (path: string): string => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const baseUrl = getApiBaseUrl();
  return `${baseUrl}${normalizedPath}`;
};

const getAuthToken = (): string | null => {
  try {
    return localStorage.getItem('accessToken');
  } catch {
    return null;
  }
};

const buildHeaders = (init?: RequestInit): Headers => {
  const headers = new Headers(init?.headers);
  const token = getAuthToken();
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return headers;
};

const parseErrorMessage = async (response: Response): Promise<string> => {
  const errorText = await response.text();
  return errorText || `Request failed with status ${response.status}`;
};

type RequestOptions = RequestInit & {
  timeoutMs?: number;
};

const requestJson = async <T>(path: string, init?: RequestOptions): Promise<T> => {
  const { timeoutMs, ...requestInit } = init ?? {};
  const abortController = new AbortController();
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  if (timeoutMs && timeoutMs > 0) {
    timeoutId = setTimeout(() => {
      abortController.abort();
    }, timeoutMs);
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path), {
      ...requestInit,
      headers: buildHeaders(requestInit),
      signal: abortController.signal,
    });
  } catch (error) {
    if ((error as Error).name === 'AbortError' && timeoutMs) {
      throw new Error(`Request timed out after ${Math.ceil(timeoutMs / 1000)} seconds`);
    }
    throw error;
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }

  if (!response.ok) {
    throw new Error(await parseErrorMessage(response));
  }
  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    return undefined as T;
  }

  return (await response.json()) as T;
};

export const apiClient = {
  getJson: <T>(path: string): Promise<T> => requestJson<T>(path),
  postJson: <T>(path: string, body: unknown, options?: { timeoutMs?: number }): Promise<T> =>
    requestJson<T>(path, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
      },
      timeoutMs: options?.timeoutMs,
    }),
  postFormJson: <T>(path: string, formData: FormData, options?: { timeoutMs?: number }): Promise<T> =>
    requestJson<T>(path, {
      method: 'POST',
      body: formData,
      timeoutMs: options?.timeoutMs,
    }),
  patchJson: <T>(path: string, body: unknown): Promise<T> =>
    requestJson<T>(path, {
      method: 'PATCH',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
      },
    }),
  deleteJson: <T>(path: string): Promise<T> =>
    requestJson<T>(path, {
      method: 'DELETE',
    }),
};
